import { IconProps, InfoIcon } from "@phosphor-icons/react"
import { ComponentType, ReactNode } from "react"

import { cn } from "shared/helpers/cn"

// TODO: move shared constants/functions to separate file
// eslint-disable-next-line react-refresh/only-export-components
export enum TooltipPosition {
    TOP = "top",
    RIGHT = "right",
    BOTTOM = "bottom",
    LEFT = "left",
}

type TooltipProps = {
    text?: string
    content?: ReactNode
    icon?: ComponentType<IconProps>
    children?: ReactNode
    className?: string
    position?: TooltipPosition
    contentClassName?: string
}

const positionClasses = {
    [TooltipPosition.TOP]: "bottom-full left-1/2 -translate-x-1/2 mb-1",
    [TooltipPosition.RIGHT]:
        "left-full top-1/2 -translate-y-1/2 ml-1 -mt-1 mb-2",
    [TooltipPosition.BOTTOM]: "top-full left-1/2 -translate-x-1/2 mt-1",
    [TooltipPosition.LEFT]: "right-full top-1/2 -translate-y-1/2 mr-1 -mt-1",
}

const arrowClasses = {
    [TooltipPosition.TOP]:
        "-bottom-1 left-1/2 -translate-x-1/2 border-t-surface-inverse border-t-4 border-x-4 border-x-transparent border-b-0",
    [TooltipPosition.RIGHT]:
        "-left-1 top-1/2 -translate-y-1/2 border-r-surface-inverse border-r-4 border-y-4 border-y-transparent border-l-0",
    [TooltipPosition.BOTTOM]:
        "-top-1 left-1/2 -translate-x-1/2 border-b-surface-inverse border-b-4 border-x-4 border-x-transparent border-t-0",
    [TooltipPosition.LEFT]:
        "-right-1 top-1/2 -translate-y-1/2 border-l-surface-inverse border-l-4 border-y-4 border-y-transparent border-r-0",
}

export function Tooltip({
    text,
    content,
    icon: IconComponent = InfoIcon,
    children,
    className,
    position = TooltipPosition.BOTTOM,
    contentClassName,
}: TooltipProps) {
    // If children provided without text or content, just return children
    if (children && !text && !content) {
        return children
    }

    // Determine tooltip content: prefer content prop, fall back to text
    const tooltipContent = content ?? text

    // If no tooltip content, return null or just children
    if (!tooltipContent) {
        return children || null
    }

    // For string text, check if it's empty
    if (typeof tooltipContent === "string" && tooltipContent.length === 0) {
        return children || null
    }

    // Use text for aria-label if it's a string, otherwise use a generic label
    const ariaLabel =
        typeof tooltipContent === "string" ? tooltipContent : undefined

    return (
        <span
            className={cn(
                "group relative inline-block cursor-help",
                className,
            )}
            aria-label={ariaLabel}
        >
            {children ? (
                children
            ) : (
                <span className="fill-text-secondary">
                    <IconComponent size={18} />
                </span>
            )}
            <span
                className={cn(
                    "bg-surface-inverse text-text-on-inverse",
                    "max-w-64 min-w-24 rounded p-1 text-xs",
                    "absolute z-100 hidden",
                    "group-hover:block",
                    // Only apply whitespace-pre-line for string content
                    typeof tooltipContent === "string" && "whitespace-pre-line",
                    "select-none",
                    positionClasses[position],
                    contentClassName,
                )}
            >
                {tooltipContent}
                <span
                    className={cn("absolute h-0 w-0", arrowClasses[position])}
                />
            </span>
        </span>
    )
}
